import { useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import Modal from './Modal'
import { EditPayDeviationModel, Employee, EmploymentContract, PayDeviation } from '../redux/types'
import InputField from './InputField'
import DatePicker from './DatePicker'
import EmployeeSelector, { EmployeeSelectorItem, formatEmployeeSelectorValue } from './EmployeeSelector'
import { isDateValid } from '../util/date'
import { Colors } from '../constants/colors'
import usePayDeviationActions from '../hooks/usePayDeviationActions'
import { useTranslation } from 'react-i18next'
import Select, { SelectOption } from './Select'
import { useAppSelector } from '../redux/hooks'
import useEmployeeActions from '../hooks/useEmployeeActions'

interface Props {
    initialValue?: PayDeviation | null
    onSaveClick?: (payday: EditPayDeviationModel) => void
    onCancelClick?: () => void
    visible: boolean
}

const emptyPayDeviation: EditPayDeviationModel = {
    externalId: '',
    externalEmployeeId: '',
    externalEmploymentContractId: null,
    grossValue: 0,
    date: '',
    description: ''
}

const EditPayDeviationModal = ({
    initialValue,
    onSaveClick,
    onCancelClick,
    visible
} : Props) => {
    const styles = useStyles()
    const { t } = useTranslation(['common', 'deviations']);

    const [deviation, setDeviation] = useState<EditPayDeviationModel>(emptyPayDeviation)
    const [selectedEmployee, setSelectedEmployee] = useState<Employee>()
    const [deviationAlreadyExists, setDeviationAlreadyExists] = useState(false)
    const [externalIdValid, setExternalIdValid] = useState(true)
    const [employeeValid, setEmployeeValid] = useState(true)
    const [employmentContractValid, setEmploymentContractValid] = useState(true)
    const [amountValid, setAmountValid] = useState(true)
    const [dateValid, setDateValid] = useState(true)

    const { payDeviationExists } = usePayDeviationActions()
    const { getEmployees } = useEmployeeActions()
    const employees = useAppSelector(state => state.employee.employees)
    const customer = useAppSelector(state => state.customer.customer)
    const useParallelEmploymentContracts = customer?.employer?.useParallelEmploymentContracts === true

    useEffect(() => {
        if (initialValue) {
            setDeviation({
                externalId: initialValue.externalId,
                externalEmployeeId: initialValue.employee.externalId,
                externalEmploymentContractId: null,
                grossValue: initialValue.grossValue.amount,
                date: initialValue.date,
                description: initialValue.description
            })
        } else {
            setDeviation(emptyPayDeviation)
            setSelectedEmployee(undefined)
        }
    }, [initialValue])

    useEffect(() => {
        if (visible && employees.length === 0) {
            getEmployees()
        }
    }, [visible])

    useEffect(() => {
        if (!initialValue) {
            return
        }

        const employee = employees.find(x => x.id === initialValue.employee.id)
        if (employee) {
            setSelectedEmployee(employee)
            setDeviation(deviation => ({
                ...deviation,
                externalEmployeeId: employee.externalId,
                externalEmploymentContractId: getDefaultExternalEmploymentContractId(employee)
            }))
        }
    }, [employees, initialValue, useParallelEmploymentContracts])

    useEffect(() => {
        if (!selectedEmployee) {
            return
        }

        if (!useParallelEmploymentContracts) {
            setDeviation(deviation => ({...deviation, externalEmploymentContractId: null}))
            setEmploymentContractValid(true)
            return
        }

        if (selectedEmployee.employmentContracts.length === 1) {
            setDeviation(deviation => {
                if (deviation.externalEmploymentContractId) {
                    return deviation
                }

                return {
                    ...deviation,
                    externalEmploymentContractId: selectedEmployee.employmentContracts[0].externalId
                }
            })
        }
    }, [selectedEmployee, useParallelEmploymentContracts])

    function getDefaultExternalEmploymentContractId(employee: Employee): string | null {
        if (!useParallelEmploymentContracts) {
            return null
        }

        return employee.employmentContracts.length === 1 ? employee.employmentContracts[0].externalId : ''
    }

    function formatEmploymentContract(employmentContract: EmploymentContract) {
        return `${employmentContract.startDateString} - ${employmentContract.endDateString ?? t('ongoing', {ns: 'common'})}`
    }

    const getEmploymentContractOptions = (): SelectOption[] => {
        const options = selectedEmployee?.employmentContracts.map(employmentContract => ({
            name: formatEmploymentContract(employmentContract),
            value: employmentContract.externalId
        })) ?? []

        if (options.length !== 1) {
            options.unshift({name: t('select', {ns: 'common'}), value: ''})
        }

        return options
    }

    const resetForm = () => {
        setDeviation(emptyPayDeviation)
        setSelectedEmployee(undefined)
        setDeviationAlreadyExists(false)
        setExternalIdValid(true)
        setEmployeeValid(true)
        setEmploymentContractValid(true)
        setAmountValid(true)
        setDateValid(true)
    }

    const handleOnSave = () => {
        if (!validateInput())
            return

        deviation && onSaveClick && onSaveClick(deviation)
        resetForm()
    }

    const validateBeforeSave = () => {
        if (!initialValue) {
            payDeviationExists(deviation.externalId, (exists: boolean) => {
                if (exists) {
                    setDeviationAlreadyExists(true)
                } else {
                    setDeviationAlreadyExists(false)
                    handleOnSave()    
                }
            })
        } else {
            handleOnSave()
        }
    }

    const handleOnCancel = () => {
        onCancelClick && onCancelClick()
        resetForm()
    }

    const handleOnChange = (propName: string, value: any) => {
        if (deviation)
            setDeviation({...deviation, [propName]: value})
    }

    const handleEmployeeSelected = (employee: EmployeeSelectorItem | null) => {
        if (employee) {
            setEmployeeValid(true)
            setEmploymentContractValid(true)
            setSelectedEmployee(employee.employee)
            setDeviation({
                ...deviation,
                externalEmployeeId: employee.employee.externalId,
                externalEmploymentContractId: getDefaultExternalEmploymentContractId(employee.employee)
            })    
        } else {
            setSelectedEmployee(undefined)
            setDeviation({...deviation, externalEmployeeId: '', externalEmploymentContractId: null})
        }
    } 

    const validateInput = () => {
        let result = true

        if (deviation.externalId == null || deviation.externalId.length < 1) {
            setExternalIdValid(false)
            result = false
        }

        if (deviation.externalEmployeeId == null || deviation.externalEmployeeId.length < 1) {
            setEmployeeValid(false)
            result = false
        }

        if (useParallelEmploymentContracts && (deviation.externalEmploymentContractId == null || deviation.externalEmploymentContractId.length < 1)) {
            setEmploymentContractValid(false)
            result = false
        }

        if (deviation.grossValue == null || Number.isNaN(deviation.grossValue)) {
            setAmountValid(false)
            result = false
        }

        if (!isDateValid(deviation.date)) {
            setDateValid(false)
            result = false
        }

        return result
    }

    return (
        <Modal
            title={initialValue != null ? t('editDeviation', {ns: 'deviations'}) : t('addDeviation', {ns: 'deviations'})}
            visible={visible}
            okButtonTitle={t('save', {ns: 'common'})}
            cancelButtonTitle={t('cancel', {ns: 'common'})}
            onHide={() => handleOnCancel()}
            onOkClick={() => validateBeforeSave()}
            onCancelClick={() => handleOnCancel()}
        >
            <div className={styles.row}>
                <InputField
                    id='externalId'
                    valid={externalIdValid}
                    disabled={initialValue != null}
                    value={deviation?.externalId ?? undefined}
                    onChange={value => {
                        setExternalIdValid(true)
                        handleOnChange('externalId', value)
                    }}
                    label={t('externalId', {ns: 'deviations'})}
                    placeholder=''
                    tooltipText={t('externalIdDescription', {ns: 'deviations'})}
                />
            </div>
            <div className={styles.row}>
                <EmployeeSelector
                    valid={employeeValid}
                    mandatory={true}
                    includeUnconnectedEmployees={false}
                    includeEmployeesWithoutDataCollectionConsent={false}
                    value={selectedEmployee ? formatEmployeeSelectorValue(selectedEmployee) : initialValue?.employee ? `${initialValue.employee.name} (${initialValue.employee.identificationNumber})` : ''}
                    onSelected={handleEmployeeSelected}
                />
            </div>
            {useParallelEmploymentContracts && selectedEmployee &&
            <div className={styles.row}>
                <Select
                    id='externalEmploymentContractId'
                    valid={employmentContractValid}
                    mandatory={true}
                    options={getEmploymentContractOptions()}
                    selectedValue={deviation.externalEmploymentContractId ?? undefined}
                    onSelect={value => {
                        setEmploymentContractValid(true)
                        handleOnChange('externalEmploymentContractId', value)
                    }}
                    label={t('employment', {ns: 'common'})}
                />
            </div>
            }
            <div className={styles.row}>
                <DatePicker 
                    valid={dateValid}
                    mandatory={true}
                    id='date'
                    label={t('date', {ns: 'common'})}
                    includeTime={false}
                    value={deviation?.date ?? undefined}
                    onChange={value => {
                        setDateValid(true)
                        handleOnChange('date', value)
                    }}
                />
            </div>
            <div className={styles.row}>
                <InputField
                    mandatory={false}
                    id='description'
                    value={deviation?.description}
                    onChange={value => handleOnChange('description', value)}
                    label={t('description', {ns: 'deviations'})}
                />
            </div>
            <div className={styles.row}>
                <InputField
                    valid={amountValid}
                    mandatory={true}
                    id='grossValue'
                    type='number'
                    value={deviation?.grossValue.toString()}
                    onChange={value => {
                        setAmountValid(true)
                        handleOnChange('grossValue', Number.parseFloat(value))
                    }}
                    label={t('grossAmount', {ns: 'deviations'})}
                    tooltipText={t('grossAmountDescription', {ns: 'deviations'})}
                />
            </div>
            
            {deviationAlreadyExists && 
            <div className={styles.errorMessage}>
                {t('deviationAlreadyExists', {ns: 'deviations'})}
            </div>
            }
        </Modal>
    )
}

export default EditPayDeviationModal

const useStyles = createUseStyles({
    row: {
        paddingBottom: 12
    },
    errorMessage: {
        color: Colors.rubel_700
    }
})
